// =============================================================================
// SimSense Platform — AI Coaching Page (OT-136 Redesign)
// =============================================================================
// Visual coaching dashboard with priority cards, collapsible sections,
// corner stoplight grid, and action plan checklist.
// =============================================================================

import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import Markdown from "react-markdown";
import { api } from "../utils/api";
import { formatLapTime } from "../utils/formatters";
import "./Coaching.css";

interface CoachingSection {
  title: string;
  icon: string;
  content: string;
}

interface CoachingReport {
  tier: "free" | "pro";
  summary?: string;
  score?: number;
  pace_delta_seconds?: number;
  technique_grades?: Record<string, string>;
  priority_checklist?: string[];
  corner_stoplights?: Record<string, Record<string, string>>;
  car_balance_status?: string;
  observations?: string[]; // for free tier
  myTrend?: any[]; // Stage 3 historical data
  referenceLaps?: any[]; // Stage 4 community best laps
  sections?: CoachingSection[];
  content?: string;
  heuristicIssues?: Record<string, string[]>;
  cornerFeatures?: Record<string, any>;
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// --- Helpers to categorize sections ---

const PRIORITY_TITLES = ["top priorities", "executive summary"];
const ACTION_TITLES = ["action plan", "development plan"];
function classifySection(title: string): "priority" | "action" | "detail" {
  const lower = title.toLowerCase();
  if (PRIORITY_TITLES.some((t) => lower.includes(t))) return "priority";
  if (ACTION_TITLES.some((t) => lower.includes(t))) return "action";
  return "detail";
}

/** Extract numbered items from markdown content for priority/action display */
function extractItems(content: string): string[] {
  const lines = content.split("\n").filter((l) => l.trim());
  const items: string[] = [];
  let current = "";

  for (const line of lines) {
    const trimmed = line.trim();
    // Match numbered items (1. 2. 3.) or bullet items (- *)
    if (/^(\d+[\.\)]\s|[-*]\s)/.test(trimmed)) {
      if (current) items.push(current);
      current = trimmed.replace(/^(\d+[\.\)]\s|[-*]\s)/, "").trim();
    } else if (current) {
      current += " " + trimmed;
    } else {
      current = trimmed;
    }
  }
  if (current) items.push(current);
  return items;
}

/** Determine severity color based on content keywords */
function getSeverityColor(text: string): string {
  const lower = (text || "").toLowerCase();
  if (
    lower.includes("critical") ||
    lower.includes("significant") ||
    lower.includes("major") ||
    lower.includes("losing") ||
    lower.includes("too late") ||
    lower.includes("too early") ||
    lower.includes("red") ||
    lower.includes("oversteer") ||
    lower.includes("bad")
  )
    return "var(--bad)";
  if (
    lower.includes("inconsistent") ||
    lower.includes("could") ||
    lower.includes("slight") ||
    lower.includes("moderate") ||
    lower.includes("yellow") ||
    lower.includes("understeer") ||
    lower.includes("warn")
  )
    return "var(--warn)";
  return "var(--good)";
}

/** Map A-F letter grades to color class names */
function getGradeColor(grade: string): "good" | "warn" | "bad" {
  const letter = (grade || "").toUpperCase().charAt(0);
  if (letter === "A") return "good";
  if (letter === "B") return "warn";
  return "bad"; // C, D, F
}

function CoachingScorecard({ score, delta, grades }: { score: number; delta?: number; grades?: Record<string, string> }) {
  const color = score > 80 ? "var(--good)" : score > 60 ? "var(--warn)" : "var(--bad)";

  return (
    <div className="coaching__scorecard">
      <div className="coaching__score-main">
        <div className="coaching__score-circle" style={{ borderColor: color }}>
          <div className="coaching__score-value" style={{ color }}>{score}</div>
          <div className="coaching__score-label">Sense Score</div>
        </div>
      </div>

      {grades && Object.keys(grades).length > 0 && (
        <div className="coaching__score-grades">
          {Object.entries(grades).map(([label, grade]) => (
            <div key={label} className="coaching__grade-item">
              <div className="coaching__grade-label">{label}</div>
              <div className={`coaching__grade-badge coaching__grade-badge--${getGradeColor(grade)}`}>
                {grade}
              </div>
            </div>
          ))}
        </div>
      )}

      {delta !== undefined && (
        <div className="coaching__score-delta">
          <div className="coaching__delta-value">+{ delta.toFixed(3) }s</div>
          <div className="coaching__delta-label">vs Benchmark</div>
        </div>
      )}
    </div>
  );
}

function HistoricalProgress({ trend }: { trend: any[] }) {
  if (!trend || trend.length < 2) return null;

  // Take last 5 sessions
  const recent = trend.slice(-5);
  
  // Get unique corners
  const corners = Array.from(new Set(recent.flatMap(s => Object.keys(s.corners)))).sort();

  return (
    <div className="coaching__historical">
      <h3 className="coaching__sidebar-title">Historical Progress</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {corners.map(corner => {
          const data = recent.map(s => s.corners[corner]?.min_speed_mph || 0).filter(v => v > 0);
          if (data.length < 2) return null;
          
          const min = Math.min(...data);
          const max = Math.max(...data);
          const range = max - min || 1;
          const current = data[data.length - 1];
          const previous = data[data.length - 2];
          const delta = current - previous;

          return (
            <div key={corner} style={{ background: "rgba(255,255,255,0.02)", padding: "12px", borderRadius: "8px", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", alignItems: "center" }}>
                <div style={{ fontSize: "12px", fontWeight: 600 }}>{corner} Apex Speed</div>
                <div style={{ fontSize: "11px", color: delta >= 0 ? "var(--good)" : "var(--bad)", fontWeight: 700 }}>
                  {delta >= 0 ? "+" : ""}{delta.toFixed(1)} mph
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", height: "30px" }}>
                {data.map((val, i) => (
                  <div 
                    key={i} 
                    style={{ 
                      flex: 1, 
                      height: `${((val - min) / range * 70) + 30}%`, 
                      background: i === data.length - 1 ? "var(--accent)" : "rgba(255,255,255,0.1)",
                      borderRadius: "2px" 
                    }} 
                    title={`${val.toFixed(1)} mph`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReferenceLibrary({ laps }: { laps: any[] }) {
  if (!laps || laps.length === 0) return null;

  const bestTime = Math.min(...laps.map(l => l.lap_time));

  return (
    <div className="coaching__reference-laps">
      <h3 className="coaching__sidebar-title">Community Best Laps</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {laps.map((lap, i) => {
          const delta = lap.lap_time - bestTime;
          return (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", padding: "8px 10px", background: lap.is_self ? "rgba(59, 130, 246, 0.1)" : "rgba(255,255,255,0.02)", borderRadius: "6px", border: "1px solid var(--border)" }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: 600, color: lap.is_self ? "var(--accent)" : "var(--text1)" }}>
                  {lap.driver} {lap.is_self && "(You)"}
                </span>
                <span style={{ fontSize: "10px", color: "var(--text3)" }}>{new Date(lap.date).toLocaleDateString()}</span>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700 }}>{formatLapTime(lap.lap_time)}</div>
                {delta > 0 && <div style={{ fontSize: "10px", color: "var(--text3)" }}>+{delta.toFixed(3)}s</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Coaching() {
  const { id, stintIndex } = useParams<{ id: string; stintIndex: string }>();
  const query = useQuery();
  const requestedDepth = query.get("depth") || "pro";

  const [report, setReport] = useState<CoachingReport | null>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    new Set()
  );

  const toggleSection = (idx: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const [sData, cData] = await Promise.all([
          api<any>(`/api/sessions/${id}`),
          api<any>(`/api/coaching/report/${id}/${stintIndex}?depth=${requestedDepth}`),
        ]);

        setSession(sData);
        // Fetch trend + best laps in parallel now that we have session data
        const [trend, laps] = await Promise.all([
          api<any[]>(`/api/insights/my-trend/${sData.trackId}/${encodeURIComponent(sData.carName)}`).catch(() => []),
          api<any[]>(`/api/leaderboard/best-laps/${encodeURIComponent(sData.trackName)}/${encodeURIComponent(sData.carName)}`).catch(() => []),
        ]);
        
        setReport({ ...cData, myTrend: trend, referenceLaps: laps });
        document.title = `Coaching — ${sData.trackName || "Session"} | SimSense`;
      } catch (err: any) {
        console.error("Failed to fetch coaching data", err);
        setError(err.message || "Failed to generate coaching report.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, stintIndex, requestedDepth]);

  // --- Loading State ---
  if (loading) {
    return (
      <div className="coaching-loading">
        <div className="coaching-loading__icon">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--accent)"
            strokeWidth="1.5"
          >
            <circle cx="12" cy="12" r="10" opacity="0.2" />
            <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round">
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 12 12"
                to="360 12 12"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        </div>
        <div className="coaching-loading__title">
          Analyzing your telemetry...
        </div>
        <div className="coaching-loading__subtitle">
          Generating coaching report (10-15s)...
        </div>
      </div>
    );
  }

  // --- Error States ---
  if (error) {
    const isUpgradeError =
      error.includes("Upgrade to Pro") || error.includes("403");

    if (isUpgradeError) {
      return (
        <div className="coaching-error coaching-error--upgrade">
          <div className="coaching-error__icon">&#128274;</div>
          <h2>Report Limit Reached</h2>
          <p>
            You've used all 3 free coaching reports this month.
            Upgrade to SimSense Pro for unlimited reports.
          </p>
          <div className="coaching-error__actions">
            <Link to={`/session/${id}`} className="coaching-btn coaching-btn--secondary">
              Back to Session
            </Link>
            <Link
              to="/pricing"
              className="coaching-btn coaching-btn--primary"
            >
              Upgrade to Pro
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="coaching-error">
        <div className="coaching-error__title">Error</div>
        <div className="coaching-error__message">{error}</div>
        <Link to={`/session/${id}`} className="coaching-error__link">
          Return to Session
        </Link>
      </div>
    );
  }

  if (!report || !session) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        No report found.
      </div>
    );
  }

  // Handle Free vs Pro
  if (report.tier === "free") {
    return (
      <div>
        <div className="coaching" style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>
          <div className="coaching__breadcrumbs">
            <Link to="/">Sessions</Link>
            <span>/</span>
            <Link to={`/session/${id}`}>{session.trackName}</Link>
            <span>/</span>
            <span>AI Spotter</span>
          </div>

          <div className="coaching__header">
            <div>
              <h2 className="coaching__title">AI Spotter Feedback</h2>
              <div className="coaching__subtitle">{session.carName} @ {session.trackName}</div>
            </div>
            <span className="coaching__badge">Free Session</span>
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: report.score !== undefined ? "240px 1fr" : "1fr", gap: "24px", marginBottom: "32px" }}>
            {report.score !== undefined && <CoachingScorecard score={report.score} />}
            <div className="coaching__summary">
              <div className="coaching__summary-label">Quick Summary</div>
              <p className="coaching__summary-text">{report.summary}</p>
            </div>
          </div>

          <div className="coaching__actions">
            <h3 className="coaching__section-title">🎯 Quick Observations</h3>
            <div className="coaching__action-list">
              {(report.observations || []).map((item, i) => (
                <div key={i} className="coaching__action-item">
                  <span className="coaching__action-text">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="coaching__upsell" style={{ marginTop: "40px" }}>
            <div className="coaching__upsell-title">Want pro telemetry analysis?</div>
            <p className="coaching__upsell-text">Upgrade to Pro for corner-by-corner stoplights, tire wear analysis, and a technical development plan.</p>
            <a href="/settings" className="coaching-btn coaching-btn--accent">Upgrade to Pro</a>
          </div>
        </div>
      </div>
    );
  }

  // Use structured checklist if available, otherwise extract from sections
  const priorityItems = report.priority_checklist && report.priority_checklist.length > 0
    ? report.priority_checklist
    : (report.sections?.find(s => classifySection(s.title) === "priority")
        ? extractItems(report.sections.find(s => classifySection(s.title) === "priority")!.content)
        : []);

  const detailSections =
    report.sections?.filter((s) => classifySection(s.title) === "detail") || [];

  const content = (
    <div className="coaching">
      {/* Breadcrumbs */}
      <div className="coaching__breadcrumbs">
        <Link to="/">Sessions</Link>
        <span>/</span>
        <Link to={`/session/${id}`}>{session.trackName}</Link>
        <span>/</span>
        <span>Coaching — Run {Number(stintIndex) + 1}</span>
      </div>

      {/* Header */}
      <div className="coaching__header">
        <div>
          <div className="coaching__header-row">
            <h2 className="coaching__title">Advanced Coaching Report</h2>
          </div>
          <div className="coaching__subtitle">
            {session.carName} @ {session.trackName}
          </div>
        </div>
        <Link
          to={`/session/${id}`}
          className="coaching-btn coaching-btn--secondary coaching-btn--sm"
        >
          Back to Session
        </Link>
      </div>

      {/* Scorecard & Summary Banner */}
      <div style={{ display: "grid", gridTemplateColumns: report.score !== undefined ? "280px 1fr" : "1fr", gap: "24px", marginBottom: "24px" }}>
        {report.score !== undefined && <CoachingScorecard score={report.score} delta={report.pace_delta_seconds} grades={report.technique_grades} />}
        {report.summary && (
          <div className="coaching__summary">
            <div className="coaching__summary-label">Executive Overview</div>
            <p className="coaching__summary-text">{report.summary}</p>
            {report.car_balance_status && (
              <div style={{ marginTop: "12px", fontSize: "12px", color: "var(--text2)", display: "flex", alignItems: "center", gap: "8px" }}>
                <span>Detected Balance:</span>
                <span style={{ 
                  padding: "2px 6px", 
                  borderRadius: "4px", 
                  background: "rgba(255,255,255,0.05)",
                  color: getSeverityColor(report.car_balance_status), 
                  textTransform: "uppercase",
                  fontWeight: 700,
                  fontSize: "10px",
                  letterSpacing: "0.5px"
                }}>{report.car_balance_status}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Grid */}
      <div className="coaching__grid">
        {/* Left Column — Main Content */}
        <div className="coaching__main">
          {/* Priority Cards */}
          {priorityItems.length > 0 && (
            <div className="coaching__priorities">
              <h3 className="coaching__section-title">
                🎯 Top Critical Priorities
              </h3>
              <div className="coaching__priority-list">
                {priorityItems.map((item, i) => (
                  <div
                    key={i}
                    className="coaching__priority-card"
                    style={{
                      borderLeftColor: getSeverityColor(item),
                    }}
                  >
                    <div className="coaching__priority-number">{i + 1}</div>
                    <div className="coaching__priority-text">
                      <Markdown>{item}</Markdown>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detail Sections (Collapsible) */}
          {detailSections.length > 0 && (
            <div className="coaching__details">
              {detailSections.map((section, idx) => {
                const isExpanded = expandedSections.has(idx);
                return (
                  <div
                    key={idx}
                    className={`coaching__detail-card ${isExpanded ? "coaching__detail-card--expanded" : ""}`}
                  >
                    <button
                      className="coaching__detail-header"
                      onClick={() => toggleSection(idx)}
                    >
                      <div className="coaching__detail-header-left">
                        <span className="coaching__detail-icon">
                          {section.icon}
                        </span>
                        <span className="coaching__detail-title">
                          {section.title}
                        </span>
                      </div>
                      <svg
                        className={`coaching__chevron ${isExpanded ? "coaching__chevron--open" : ""}`}
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    {isExpanded && (
                      <div className="coaching__detail-body coaching-content">
                        <Markdown>{section.content}</Markdown>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Action Plan Checklist */}
          {priorityItems.length > 0 && (
            <div className="coaching__actions">
              <h3 className="coaching__section-title">
                ✅ Next Session To-Do
              </h3>
              <div className="coaching__action-list">
                {priorityItems.map((item, i) => (
                  <label key={i} className="coaching__action-item">
                    <input type="checkbox" className="coaching__checkbox" />
                    <span className="coaching__action-text">
                      <Markdown>{item}</Markdown>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column — Sidebar */}
        <div className="coaching__sidebar">
          {/* Corner Stoplight Grid */}
          {(report.corner_stoplights || report.cornerFeatures) && (
              <div className="coaching__corner-grid">
                <h3 className="coaching__sidebar-title">Corner Entry/Apex/Exit</h3>
                <div className="coaching__corners">
                  {Object.entries(report.corner_stoplights || report.cornerFeatures || {}).map(
                    ([corner]: [string, any]) => {
                      const stoplights = report.corner_stoplights?.[corner];
                      
                      return (
                        <div key={corner} className="coaching__corner-row">
                          <div className="coaching__corner-name">
                            {corner}
                          </div>
                          <div className="coaching__corner-stoplights">
                            {stoplights ? (
                              <div style={{ display: "flex", gap: "6px" }}>
                                <span className={`coaching__corner-dot coaching__corner-dot--${stoplights.entry}`} title="Entry" />
                                <span className={`coaching__corner-dot coaching__corner-dot--${stoplights.apex}`} title="Apex" />
                                <span className={`coaching__corner-dot coaching__corner-dot--${stoplights.exit}`} title="Exit" />
                              </div>
                            ) : (
                              <span className="coaching__corner-dot coaching__corner-dot--warn" />
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
                <Link
                  to={`/session/${id}/corners`}
                  className="coaching__corner-link"
                >
                  View Full Corner Analysis
                </Link>
              </div>
            )}

          {/* Historical Progress [OT-150 Stage 3] */}
          {report.myTrend && report.myTrend.length >= 2 && (
            <HistoricalProgress trend={report.myTrend} />
          )}

          {/* Community Best Laps [OT-154 Stage 4] */}
          {report.referenceLaps && report.referenceLaps.length > 0 && (
            <ReferenceLibrary laps={report.referenceLaps} />
          )}

          {/* Setup AI Teaser */}
          <div className="coaching__teaser">
            <h3 className="coaching__teaser-title">Setup AI</h3>
            <span className="coaching__teaser-badge">Coming Soon</span>
            <p className="coaching__teaser-text">
              Our future Setup AI will analyze tire wear and telemetry to
              provide exact adjustments and a ready-to-race .sto file.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>{content}</div>
  );
}
